import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import DataGrid from '../../../components/common/dataGrid/dataGrid';
import DataGridHeader from '../../../components/common/dataGrid/dataGridAddPatientHeader';
import { useForm } from "react-hook-form";
import { DevTool } from "@hookform/devtools";
import { commonPostApi } from "../../../utils/apiService";
import { API_ENDPOINTS } from "../../../utils/apiEndpoints";
import { convertToSearchField, generateColumnMetadata, orderObj, mask } from "../../../utils/utils";
import { nonRequirePatientListField, PatientListTableColumns } from "../../../utils/serviceData";
import { SORT_ORDER } from "../../../constants/sortOrder";

const searchFields = ["patient_name", "patient_code", "phone_no","dob","gender"];

const PatientSearchModal = ({ visible, onHide, onPatientSelect }) => {
  const { control } = useForm();
  const [patients, setPatients] = useState([]);
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [navigator, setNavigator] = useState({
    page: 1,
    first: 0,
    rows: 10,
    totalRecords: 0,
    sortBy: "patient_name",
    sortDir: SORT_ORDER.ASC,
    sortFields: [["patient_name", SORT_ORDER.ASC]],
    searchCriteria: convertToSearchField(searchFields, ""),
  });

  const init = async (navigator) => {
    setLoading(true);
    const query = {
      page: navigator.page,
      limit: navigator.rows,
      sortFields: [[navigator.sortBy, navigator.sortDir]],
      searchCriteria: navigator.searchCriteria,
    };
    try {
      const response = await commonPostApi(API_ENDPOINTS.PATIENT_DETAILS, { query });
      const data = response?.data?.data || [];
      const total = response?.data?.total || 0;
      const currentPage = response?.data?.currentPage || 1;



      const updatedValues = data.map((p) => ({
        ...p,
        aadhar_no: mask(p.aadhar_no),
      }));


      setNavigator((prev) => ({
        ...prev,
        totalRecords: total,
        page: currentPage,
      }));

      const transformedData = generateColumnMetadata({
        data: data,
        nonRequireField: nonRequirePatientListField,
        alias: {
          patient_name: "PATIENT",
          patient_code: "PATIENT ID",
          aadhar_no: "AADHAAR",
          gender: "GENDER",
          dob: "DOB",
          phone_no: "PHONE NO",
        },
        defaultColumnsArr:PatientListTableColumns
      });



      const orderedPatients = orderObj(
        ["patient_name", "patient_code", "aadhar_no", "gender", "dob", "phone_no"],
        transformedData
      );



      setPatients(orderedPatients);
      setValues(updatedValues);
    } catch (error) {
      console.error("Error fetching patients:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleHide = () => {
    setPatients([]);
    setValues([]);
    setNavigator({
      page: 1,
      first: 0,
      rows: 10,
      totalRecords: 0,
      sortBy: "patient_name",
      sortDir: SORT_ORDER.ASC,
      sortFields: [["patient_name", SORT_ORDER.ASC]],
      searchCriteria: convertToSearchField(searchFields, ""),
    });
    onHide();
  };

  useEffect(() => {
    if (visible) {
      init(navigator);
    }
  }, [visible]);

  const handleSearchChange = (value) => {
    setNavigator((prev) => ({
      ...prev,
      searchCriteria: convertToSearchField(searchFields, value),
    }));
    init({
      ...navigator,
      searchCriteria: convertToSearchField(searchFields, value),
    });
  };

  const onPageChange = (event) => {
    setNavigator((prev) => ({
      ...prev,
      page: event.page + 1,
      first: event.first,
      rows: event.rows,
    }));
    init({
      ...navigator,
      first: event?.first,
      page: event.page + 1,
    });
  };

  const onRowCountChange = (e) => {
    setLoading(true);
    setNavigator((prev) => ({
      ...prev,
      rows: e?.target?.value,
      page: 1,
      first: 0,
    }));
    init({
      ...navigator,
      page: 1,
      first: 0,
      rows: e?.target?.value,
    });
  };

  const onSortClick = (event) => {
    setLoading(true);
    const sortOrder = event?.sortOrder === 1 ? SORT_ORDER.ASC : SORT_ORDER.DESC;
    setNavigator((prev) => ({
      ...prev,
      sortBy: event?.sortField,
      sortDir: sortOrder,
      sortFields: [[event?.sortField, sortOrder]],
    }));
    init({
      ...navigator,
      sortBy: event?.sortField,
      sortDir: sortOrder,
      sortFields: [[event?.sortField, sortOrder]],
    });
  };

  const onRowClick = (event) => {
    onPatientSelect(event.data);
    handleHide();
  };

  return (
    <Dialog
      visible={visible}
      onHide={handleHide}
      style={{ width: "90vw", maxWidth: "1200px" }}
      modal
      draggable={false}
      resizable={false}
    >
      <div className="patient-search-content">
        <DataGrid
          header={
            <DataGridHeader
              isSearchEnabled={true}
              isDownloadButton={false}
              isCalenderEnabled={false}
              isButtonEnabledLeft={false}
              isButtonEnabledRight={false}
              searchPlaceHolder="Search Patients"
              currentPageHeader="Patient List"
              onChange={handleSearchChange}
            />
          }
          column={patients}
          value={values}
          resizableColumns={true}
          showDownloadButton={false}
          navigator={navigator}
          onSortClick={onSortClick}
          onPageChange={onPageChange}
          onRowCountChange={onRowCountChange}
          onRowClick={onRowClick}
          loading={loading}
          sortable={true}
          tableClassName="wrap-columns"
          isAccordionEnabled={false}
          isPaginatorEnabled={true}
        />
      </div>
      <DevTool control={control} />
    </Dialog>
  );
};

export default PatientSearchModal;